import { useEffect, useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Users, ShieldCheck, HardHat, UserCircle2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * RegionalCommandBoard — Master Architecture Feb 2026.
 *
 * The Regional Manager's single-line 3-column cockpit:
 *   ▸ Column 1 — Supervisors (role=supervisor) in the RM's region
 *   ▸ Column 2 — Crew Leaders (role=crew_leader) in the RM's region
 *   ▸ Column 3 — Site Agents (role=agent) in the RM's region
 *
 * When a Supervisor card is clicked, columns 2 & 3 filter down to only that
 * supervisor's downstream chain (via the `managed_by` links on
 * staff_assignments) — this is the "Regional Assignment Card" preview.
 *
 * Each card links back up the `managed_by` chain: a Crew Leader shows their
 * Supervisor's name; an Agent shows their Crew Leader's name. Every card
 * also carries the person's daily counters (pickups / violations / no-
 * trashes) fetched from `/api/rewards/daily/{user_id}`.
 */
export function RegionalCommandBoard({ user, regionId, embedded = false }) {
  const [staff, setStaff] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [rewards, setRewards] = useState({});
  const [loading, setLoading] = useState(true);
  // Iter-78 pt 5 — click a Supervisor card to spotlight only their downstream
  // chain (Crew Leaders they manage → Agents those CLs manage). Click again
  // or hit the ✕ chip to clear back to full-region view.
  const [pinnedSupervisor, setPinnedSupervisor] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const scope = regionId || user?.region_id;
    (async () => {
      setLoading(true);
      try {
        const [teamRes, assignRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/routes/team-view`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/company/staff-assignments`, { credentials: 'include' }),
        ]);
        const teamData = teamRes.ok ? await teamRes.json() : [];
        const assignData = assignRes.ok ? await assignRes.json() : [];
        if (cancelled) return;

        // Select the target region (own region for RM, first if admin without match)
        const entries = (teamData || []).filter(
          (e) => e.region?.region_id && e.region.region_id !== '_unassigned',
        );
        const target = scope
          ? entries.find((e) => e.region.region_id === scope) || entries[0]
          : entries[0];
        setStaff(target?.staff || []);
        setAssignments(Array.isArray(assignData) ? assignData : []);
      } catch (_) { /* silent */ }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user?.region_id, regionId]);

  const assignByUserId = useMemo(() => {
    const map = {};
    for (const a of assignments) {
      if (a?.user_id) map[a.user_id] = a;
    }
    return map;
  }, [assignments]);

  // Fetch daily rewards for every staff member (in parallel, capped).
  useEffect(() => {
    if (!staff.length) return;
    const ids = staff.map((s) => s.user_id).slice(0, 120);
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        ids.map((uid) =>
          fetch(`${BACKEND_URL}/api/rewards/daily/${uid}`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
        ),
      );
      if (cancelled) return;
      const map = {};
      results.forEach((r, i) => { if (r) map[ids[i]] = r; });
      setRewards(map);
    })();
    return () => { cancelled = true; };
  }, [staff]);

  const supervisors = staff.filter((s) => s.role === 'supervisor');
  const allCrewLeaders = staff.filter((s) => s.role === 'crew_leader');
  const allAgents = staff.filter((s) => s.role === 'agent');

  // When a Supervisor is pinned, filter downstream tiers to only that
  // supervisor's chain (Crew Leaders where managed_by === supervisor,
  // then Agents where managed_by ∈ those Crew Leader ids).
  const downstreamCrewLeaderIds = useMemo(() => {
    if (!pinnedSupervisor) return null;
    return allCrewLeaders
      .filter((cl) => assignByUserId[cl.user_id]?.managed_by === pinnedSupervisor)
      .map((cl) => cl.user_id);
  }, [pinnedSupervisor, allCrewLeaders, assignByUserId]);

  const crewLeaders = pinnedSupervisor
    ? allCrewLeaders.filter((cl) => downstreamCrewLeaderIds.includes(cl.user_id))
    : allCrewLeaders;

  const agents = pinnedSupervisor
    ? allAgents.filter((a) => downstreamCrewLeaderIds.includes(assignByUserId[a.user_id]?.managed_by))
    : allAgents;

  const pinnedSupervisorName =
    pinnedSupervisor && supervisors.find((s) => s.user_id === pinnedSupervisor)?.name;

  const parentName = (u) => {
    const a = assignByUserId[u.user_id];
    return a?.managed_by_name || a?.assigned_by_name || null;
  };

  const memberCard = (person, tint, IconEl, testidPrefix) => {
    const r = rewards[person.user_id] || {};
    const parent = parentName(person);
    const isPinned = testidPrefix === 'supervisors' && pinnedSupervisor === person.user_id;
    const clickable = testidPrefix === 'supervisors';
    return (
      <Card
        key={person.user_id}
        className={`p-3 bg-zinc-900 border transition hover:-translate-y-0.5 ${tint} ${
          clickable ? 'cursor-pointer' : ''
        } ${isPinned ? 'ring-2 ring-[#FFD60A] shadow-[0_0_0_2px_rgba(255,214,10,0.35)]' : ''}`}
        data-testid={`${testidPrefix}-card-${person.user_id}`}
        data-pinned={isPinned ? 'true' : 'false'}
        onClick={clickable ? () => setPinnedSupervisor(isPinned ? null : person.user_id) : undefined}
      >
        <div className="flex items-start gap-2 mb-2">
          <IconEl className="h-4 w-4 mt-0.5 opacity-80 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-white truncate leading-tight">{person.name || 'Unnamed'}</p>
            {person.badge_number ? (
              <p className="text-[10px] text-zinc-500 mt-0.5">{person.badge_number}</p>
            ) : null}
            {parent ? (
              <p className="text-[10px] text-zinc-400 mt-1">
                <span className="opacity-60">Managed by</span> <span className="text-white">{parent}</span>
              </p>
            ) : null}
          </div>
          {isPinned && (
            <Badge className="bg-[#FFD60A] text-black text-[9px] px-1.5 py-0 font-black">PINNED</Badge>
          )}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <Badge className="bg-emerald-600/20 text-emerald-300 border border-emerald-500/40 text-[10px] px-1.5 py-0">
            {r.pickups ?? 0} pickups
          </Badge>
          <Badge className="bg-red-600/20 text-red-300 border border-red-500/40 text-[10px] px-1.5 py-0">
            {r.violations ?? 0} viol
          </Badge>
          <Badge className="bg-zinc-700 text-zinc-300 border border-zinc-600 text-[10px] px-1.5 py-0">
            {r.no_trashes ?? 0} no-trash
          </Badge>
        </div>
      </Card>
    );
  };

  const columns = [
    {
      key: 'supervisors',
      title: `(${supervisors.length}) Supervisors`,
      icon: ShieldCheck,
      tint: 'border-[#C9A962]/40',
      cardTint: 'border-[#C9A962]/40',
      list: supervisors,
    },
    {
      key: 'crew-leaders',
      title: `(${crewLeaders.length}) Crew Leaders`,
      icon: HardHat,
      tint: 'border-purple-500/40',
      cardTint: 'border-purple-500/40',
      list: crewLeaders,
    },
    {
      key: 'site-agents',
      title: `(${agents.length}) Site Agents`,
      icon: UserCircle2,
      tint: 'border-orange-500/40',
      cardTint: 'border-orange-500/40',
      list: agents,
    },
  ];

  return (
    <Card
      className={`p-4 mb-6 bg-black border-2 border-[#C9A962]/60 ${embedded ? 'mb-2' : ''}`}
      data-testid="regional-command-board"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3
          className="font-bold text-lg flex items-center text-white"
          style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.06em' }}
        >
          <Users className="h-5 w-5 mr-2 text-[#C9A962]" />
          {pinnedSupervisor ? 'REGIONAL ASSIGNMENT CARD' : 'REGIONAL COMMAND BOARD'}
          <Badge className="ml-2 bg-[#C9A962] text-black text-[10px] font-bold">
            {supervisors.length + crewLeaders.length + agents.length} in scope
          </Badge>
          {pinnedSupervisor && (
            <button
              type="button"
              onClick={() => setPinnedSupervisor(null)}
              className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#FFD60A] bg-[#FFD60A]/10 text-[#FFD60A] text-[10px] font-bold hover:bg-[#FFD60A]/20"
              data-testid="regional-command-clear-pin"
            >
              <X className="h-3 w-3" />
              {pinnedSupervisorName || 'Supervisor'} · clear
            </button>
          )}
        </h3>
        <span className="text-[10px] text-zinc-500 uppercase tracking-widest">
          {pinnedSupervisor
            ? `Filtered to ${pinnedSupervisorName}'s crew`
            : 'Tap a Supervisor · Chain of command'}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="regional-command-columns">
        {columns.map((col) => {
          const IconEl = col.icon;
          return (
            <div
              key={col.key}
              className={`rounded-lg border ${col.tint} p-3 bg-zinc-950/40 min-h-[220px]`}
              data-testid={`regional-command-column-${col.key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <p
                  className="text-xs uppercase tracking-widest text-white font-black flex items-center gap-1.5"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  <IconEl className="h-4 w-4 text-[#C9A962]" />
                  {col.title}
                </p>
                <Badge className="bg-zinc-800 text-zinc-300 text-[10px]">{col.list.length}</Badge>
              </div>
              {loading ? (
                <p className="text-[11px] text-zinc-500 italic">Loading…</p>
              ) : col.list.length === 0 ? (
                <p className="text-[11px] text-zinc-500 italic">
                  No {col.title.toLowerCase()} in your region yet.
                </p>
              ) : (
                <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                  {col.list.map((p) => memberCard(p, '', IconEl, col.key))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default RegionalCommandBoard;
